import { isProxy, toRaw } from "vue";
import { DateTime } from "luxon";

function equalValue(current: unknown, baseline: unknown, seen: WeakMap<object, object>): boolean {
  if (Object.is(current, baseline)) {
    return true;
  }

  if (current == null || baseline == null) {
    return false;
  }

  if (typeof current !== "object" || typeof baseline !== "object") {
    return false;
  }

  const rawCurrent = isProxy(current) ? toRaw(current) : current;
  const rawBaseline = isProxy(baseline) ? toRaw(baseline) : baseline;

  if (rawCurrent instanceof Date || rawBaseline instanceof Date) {
    return (
      rawCurrent instanceof Date &&
      rawBaseline instanceof Date &&
      rawCurrent.getTime() === rawBaseline.getTime()
    );
  }

  if (DateTime.isDateTime(rawCurrent) || DateTime.isDateTime(rawBaseline)) {
    return (
      DateTime.isDateTime(rawCurrent) &&
      DateTime.isDateTime(rawBaseline) &&
      rawCurrent.equals(rawBaseline)
    );
  }

  const currentIsArray = Array.isArray(rawCurrent);
  if (currentIsArray !== Array.isArray(rawBaseline)) {
    return false;
  }

  if (
    !currentIsArray &&
    (Object.getPrototypeOf(rawCurrent) !== Object.prototype ||
      Object.getPrototypeOf(rawBaseline) !== Object.prototype)
  ) {
    return false;
  }

  if (seen.has(rawCurrent)) {
    throw new TypeError("Cyclic validation values are not supported");
  }
  seen.set(rawCurrent, rawBaseline);

  const currentKeys = Object.keys(rawCurrent);
  const baselineKeys = Object.keys(rawBaseline);
  if (currentKeys.length !== baselineKeys.length) {
    return false;
  }

  return currentKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(rawBaseline, key) &&
      equalValue(
        (rawCurrent as Record<string, unknown>)[key],
        (rawBaseline as Record<string, unknown>)[key],
        seen,
      ),
  );
}

export function isValidationValueEqual(current: unknown, baseline: unknown): boolean {
  return equalValue(current, baseline, new WeakMap());
}
